import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductRail from '../components/ProductRail';
import { absMediaUrl } from '../components/ProductCard';
import CountryBanner from '../components/CountryBanner';
import TrendingReels from '../components/TrendingReels';
import ShopTheRail from '../components/ShopTheRail';
import { HERO_BANNERS, CATEGORY_TILES, SHOP_CATEGORIES, BRAND_COLLABS } from '../mock/mock';
import { fetchProducts } from '../api/client';
import { cdnImage } from '../utils/cdnImage';
import { useLocale } from '../context/LocaleContext';
import { useTaxonomies } from '../context/TaxonomiesContext';

const HeroCarousel = () => {
  const [idx, setIdx] = useState(0);
  const { t } = useLocale();
  useEffect(() => {
    const timer = setInterval(() => setIdx((prev) => (prev + 1) % HERO_BANNERS.length), 5000);
    return () => clearInterval(timer);
  }, []);
  const b = HERO_BANNERS[idx];
  const prev = () => setIdx((i) => (i - 1 + HERO_BANNERS.length) % HERO_BANNERS.length);
  const next = () => setIdx((i) => (i + 1) % HERO_BANNERS.length);
  return (
    <div className="relative h-[420px] md:h-[560px] overflow-hidden bg-gray-900">
      {HERO_BANNERS.map((banner, i) => {
        const hero = cdnImage(absMediaUrl(banner.image), 1600, { widths: [640, 960, 1280, 1600], sizes: '100vw', quality: 72 });
        return (
          <div key={banner.id} className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}>
            <img
              src={hero.src}
              srcSet={hero.srcSet}
              sizes={hero.sizes}
              alt={t(banner.titleKey)}
              width={1600}
              height={900}
              className="w-full h-full object-cover"
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchpriority={i === 0 ? 'high' : 'low'}
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
          </div>
        );
      })}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-8 flex flex-col justify-center text-white">
        <div className="max-w-lg">
          <div className="text-xs md:text-sm tracking-[0.3em] mb-3 opacity-90">{t('presents')}</div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">{t(b.titleKey)}</h1>
          {(() => { const s = t(b.subKey); return s && s !== b.subKey ? <p className="text-base md:text-xl mt-3 opacity-90">{s}</p> : null; })()}
          <Link to={b.link} className="inline-block mt-6 bg-[#39ff14] text-black px-8 py-3 font-black tracking-wider text-sm border-2 border-black hover:bg-[#22c55e] hover:text-white hover:border-[#22c55e] transition-colors">{t(b.ctaKey)}</Link>
        </div>
      </div>
      <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition"><ChevronLeft /></button>
      <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition"><ChevronRight /></button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {HERO_BANNERS.map((banner, i) => (
          <button key={banner.id} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-white' : 'w-4 bg-white/50'}`} />
        ))}
      </div>
    </div>
  );
};

const CategorySplit = () => {
  const { t } = useLocale();
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-1 mt-1">
      {CATEGORY_TILES.map((c) => {
        const tile = cdnImage(c.image, 640, { widths: [480, 640, 960], sizes: '(max-width: 768px) 100vw, 33vw' });
        return (
          <Link key={c.id} to={c.link} className="relative h-[360px] overflow-hidden group">
            <img
              src={tile.src}
              srcSet={tile.srcSet}
              sizes={tile.sizes}
              alt={t(c.id)}
              width={640}
              height={720}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-[#39ff14] px-8 py-3 border-2 border-black shadow-lg">
                <span className="text-2xl font-black tracking-widest text-black">{t(c.id)}</span>
              </div>
            </div>
          </Link>
        );
      })}
    </section>
  );
};

const ShopByCategory = () => {
  const { t } = useLocale();
  const { categories } = useTaxonomies();
  // Fall back to hardcoded tiles until the DB responds (avoids blank slate on first paint)
  const list = categories.length > 0 ? categories : SHOP_CATEGORIES.map((c) => ({ id: c.id, slug: c.id, label: c.label, image: c.image }));
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 my-14">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-black">{t('shopByCategory')}</h2>
        <div className="h-1 w-16 bg-[#22c55e] mx-auto mt-2"></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {list.map((c) => (
          <Link key={c.id || c.slug} to={`/shop/all/${c.slug || c.id}`} className="group" data-testid={`category-tile-${c.slug || c.id}`}>
            <div className="aspect-square overflow-hidden bg-gray-100 rounded">
              {c.image ? (() => {
                const tile = cdnImage(c.image, 480, { widths: [240, 320, 480, 640], sizes: '(max-width: 768px) 50vw, 25vw' });
                return (
                  <img
                    src={tile.src}
                    srcSet={tile.srcSet}
                    sizes={tile.sizes}
                    alt={c.label}
                    width={480}
                    height={480}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                );
              })() : (
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-100 flex items-center justify-center text-4xl font-black text-gray-400">{(c.label || '?').slice(0,1)}</div>
              )}
            </div>
            <p className="text-center mt-3 font-bold text-sm tracking-wide group-hover:text-[#22c55e] transition">{c.label}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const BrandCollabs = () => {
  const { t } = useLocale();
  const { collections } = useTaxonomies();
  const list = collections.length > 0 ? collections : BRAND_COLLABS.map((b) => ({ id: b.id, slug: b.name.toLowerCase(), label: b.name, image: b.image }));
  return (
    <section className="bg-black text-white py-14 mt-14">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-black">{t('officialMerch')}</h2>
          <p className="text-sm text-gray-300 mt-2">{t('officialMerchSub')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {list.map((b) => (
            <Link key={b.id || b.slug} to={`/shop/collab/${b.slug || (b.label || '').toLowerCase()}`} className="group relative overflow-hidden aspect-square rounded" data-testid={`collab-tile-${b.slug || b.id}`}>
              {b.image ? (() => {
                const tile = cdnImage(b.image, 360, { widths: [240, 320, 480], sizes: '(max-width: 768px) 50vw, 16vw' });
                return (
                  <img
                    src={tile.src}
                    srcSet={tile.srcSet}
                    sizes={tile.sizes}
                    alt={b.label}
                    width={360}
                    height={360}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                );
              })() : (
                <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900" />
              )}
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-black text-lg tracking-wider text-center px-2">{b.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { t } = useLocale();
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const na = await fetchProducts({ sort: 'new', limit: 8 });
        setNewArrivals(na.items || []);
        const bs = await fetchProducts({ sort: 'disc', limit: 8 });
        setBestSellers(bs.items || []);
      } catch (err) {
        console.warn('[Home] product fetch failed:', err.message);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CountryBanner />
      <HeroCarousel />
      <TrendingReels />
      <ShopTheRail />
      <CategorySplit />
      <ShopByCategory />
      <ProductRail title={t('newArrivals')} products={newArrivals} seeAllLink="/shop/new" />
      <BrandCollabs />
      <ProductRail title={t('bestSellers')} products={bestSellers} seeAllLink="/shop/best" />
      <Footer />
    </div>
  );
};

export default Home;
